/**
 * Which column in a company's sheet means what.
 *
 * In the single-tenant product this was fixed: an ordered list of header rules
 * decided, at import time, which column was which, and nobody saw the decision.
 * It was written most-specific-first for a good reason — a generic rule once
 * claimed a column called "Customer Emails Id" that held *staff* addresses, and
 * imported 3,991 of them as customers' emails.
 *
 * Here the same rules survive as a **suggestion**, and the company confirms it.
 * The saved map is what an import obeys; this only fills the form in. That
 * distinction is the whole lesson from the 3,991 addresses: guessing is fine,
 * guessing invisibly is not.
 */
import {
    DEFAULT_AMOUNT_MODEL, amountModelDefinition, type AmountModel,
} from './amountModel.js';

export type SheetField =
    | 'company'
    | 'sourceKey'
    | 'total'
    | 'totalType'
    | 'debitAmount'
    | 'creditAmount'
    | 'ageing1' | 'ageing2' | 'ageing3' | 'ageing4'
    | 'over90'
    | 'dueOver45'
    | 'ownerCode'
    | 'contactPerson'
    | 'contactNumber'
    | 'contactPost'
    | 'email'
    | 'city'
    | 'state'
    | 'address'
    | 'taxId'
    | 'secondaryTaxId'
    | 'category'
    | 'creditLimit'
    | 'paymentTermsDays';

export type FieldRequirement = 'required' | 'recommended' | 'optional';

export interface FieldDefinition {
    field: SheetField;
    label: string;
    requirement: FieldRequirement;
    /** Said on the mapping screen, so a company can decide rather than guess. */
    lostWithout: string;
}

/**
 * Two required fields and no more.
 *
 * A name and an amount is a receivables sheet. Everything else improves the
 * product and none of it should stop a company getting started — a sheet with
 * no ageing columns still tells them who owes what.
 */
export const SHEET_FIELDS: readonly FieldDefinition[] = [
    { field: 'company', label: 'Customer name', requirement: 'required', lostWithout: 'An account has to be called something.' },
    { field: 'total', label: 'Outstanding amount', requirement: 'required', lostWithout: 'There is nothing to collect without it.' },

    { field: 'sourceKey', label: 'Customer code', requirement: 'recommended', lostWithout: 'Accounts are matched by name instead, so two customers with the same name become one.' },
    { field: 'totalType', label: 'Dr / Cr column', requirement: 'optional', lostWithout: 'Only used when the sheet keeps the direction in its own column.' },
    { field: 'debitAmount', label: 'Debit column', requirement: 'optional', lostWithout: 'Only used when the sheet keeps debits and credits in two columns.' },
    { field: 'creditAmount', label: 'Credit column', requirement: 'optional', lostWithout: 'Only used when the sheet keeps debits and credits in two columns.' },
    { field: 'ageing1', label: 'Ageing: newest bucket', requirement: 'recommended', lostWithout: 'No ageing breakdown and no ageing report.' },
    { field: 'ageing2', label: 'Ageing: second bucket', requirement: 'recommended', lostWithout: 'No ageing breakdown and no ageing report.' },
    { field: 'ageing3', label: 'Ageing: third bucket', requirement: 'recommended', lostWithout: 'No ageing breakdown and no ageing report.' },
    { field: 'ageing4', label: 'Ageing: oldest bucket', requirement: 'recommended', lostWithout: 'No ageing breakdown and no ageing report.' },
    { field: 'ownerCode', label: 'Who owns the account', requirement: 'recommended', lostWithout: 'Accounts arrive unassigned and are given out by hand.' },

    { field: 'over90', label: 'Over 90 total', requirement: 'optional', lostWithout: 'Calculated from the ageing buckets instead.' },
    { field: 'dueOver45', label: 'Overdue over 45', requirement: 'optional', lostWithout: 'Calculated from the ageing buckets instead.' },
    { field: 'contactPerson', label: 'Contact person', requirement: 'optional', lostWithout: 'Entered in the app instead.' },
    { field: 'contactNumber', label: 'Phone', requirement: 'optional', lostWithout: 'Entered in the app instead.' },
    { field: 'contactPost', label: 'Designation', requirement: 'optional', lostWithout: '' },
    { field: 'email', label: 'Email', requirement: 'optional', lostWithout: 'Entered in the app instead.' },
    { field: 'city', label: 'City', requirement: 'optional', lostWithout: '' },
    { field: 'state', label: 'State', requirement: 'optional', lostWithout: '' },
    { field: 'address', label: 'Address', requirement: 'optional', lostWithout: '' },
    { field: 'taxId', label: 'Tax identifier', requirement: 'optional', lostWithout: '' },
    { field: 'secondaryTaxId', label: 'Second tax identifier', requirement: 'optional', lostWithout: '' },
    { field: 'category', label: 'Business type', requirement: 'optional', lostWithout: '' },
    { field: 'creditLimit', label: 'Credit limit', requirement: 'optional', lostWithout: '' },
    { field: 'paymentTermsDays', label: 'Payment terms (days)', requirement: 'optional', lostWithout: '' },
];

export const REQUIRED_FIELDS: readonly SheetField[] =
    SHEET_FIELDS.filter(f => f.requirement === 'required').map(f => f.field);

export const fieldDefinition = (field: SheetField): FieldDefinition | undefined =>
    SHEET_FIELDS.find(f => f.field === field);

/** field → the exact header text in this company's sheet. */
export type ColumnMap = Partial<Record<SheetField, string>>;

/** Headers compared the way people actually differ: spacing, case, punctuation. */
export const headerKey = (raw: string): string =>
    (raw || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Ordered most-specific-first, and the order is the whole design.
 *
 * `field: null` entries match **first** and claim nothing: they exist so that a
 * column which looks like a customer field but is not one — our own staff
 * addresses wearing the word "email" — is excluded before a generic rule can
 * take it.
 *
 * `any` matches anywhere in the key; `exact` only the whole key, for a label
 * too short to be safe as a fragment.
 */
const HEADER_RULES: { field: SheetField | null; any: string[]; exact?: string[] }[] = [
    // Ours, wearing a customer field's name. Must be first.
    { field: null, any: ['salesemail', 'staffemail', 'ouremail', 'companyemail', 'internalemail'] },
    // A ledger side ("DEBTOR" for every row) is not a business category.
    { field: null, any: ['customertype', 'ledgertype', 'accounttype'] },

    // Who owns the account, before anything matching "person".
    { field: 'ownerCode', any: ['salesperson', 'salesman', 'salesexecutive', 'salesname', 'crm', 'accountowner', 'executive', 'assignedto', 'owner'] },

    { field: 'sourceKey', any: ['customercode', 'partycode', 'accountcode', 'clientcode', 'customerid', 'partyid', 'accountno', 'accountnumber', 'ledgercode', 'code'] },

    // The ageing buckets. Numeric ranges first, then ordinal wording.
    { field: 'ageing1', any: ['045', '030', '130', '145', '0to45', 'current', 'notdue'] },
    { field: 'ageing2', any: ['4690', '3160', '4660', '6190'] },
    { field: 'ageing3', any: ['91135', '6190', '91120', '90120'] },
    // The oldest bucket is written from either edge: "Above 135" or "136+ Days".
    // A bare "136" is matched only as the whole header ("136+", "> 136"):
    // as a fragment it would take a "Balance as on 13-6-2026" column.
    {
        field: 'ageing4',
        any: ['above135', 'over135', '135', '136days', '136above', '136andabove', '136plus', 'above136', 'over136',
            'morethan136', 'above120', 'over120', 'above180', 'over180'],
        exact: ['136'],
    },

    { field: 'over90', any: ['above90', 'over90', 'morethan90', '90plus'] },
    { field: 'dueOver45', any: ['overdue45', 'above45', 'over45', 'dueover45'] },

    { field: 'totalType', any: ['drcr', 'crdr', 'balancetype', 'drcrtype'] },

    // These two sit **above** the debit/credit columns on purpose. The rules
    // match on `includes`, and "Credit Limit" contains "credit"; without this
    // ordering a company's credit limit would be read as the credit half of
    // their balance, which is a number in the book that nobody owes.
    { field: 'creditLimit', any: ['creditlimit', 'limitamount', 'sanctionedlimit'] },
    { field: 'paymentTermsDays', any: ['creditday', 'creditperiod', 'paymentterm', 'term'] },

    { field: 'debitAmount', any: ['debitamount', 'dramount', 'debit'] },
    { field: 'creditAmount', any: ['creditamount', 'cramount', 'credit'] },
    { field: 'total', any: ['closingbalance', 'outstanding', 'balanceamount', 'totaldue', 'amountdue', 'netbalance', 'balance', 'total', 'amount'] },

    { field: 'email', any: ['email', 'mail'] },
    { field: 'category', any: ['category', 'segment', 'businesstype'] },
    { field: 'taxId', any: ['gstin', 'gstno', 'gst', 'taxid', 'vatno', 'vat', 'taxnumber', 'ein', 'abn'] },
    { field: 'secondaryTaxId', any: ['pan', 'secondarytax', 'taxid2'] },
    { field: 'contactPost', any: ['designation', 'post', 'position'] },
    { field: 'contactNumber', any: ['mobile', 'phone', 'contactno', 'contactnumber', 'tel', 'whatsapp'] },
    { field: 'address', any: ['address', 'street'] },
    { field: 'city', any: ['city', 'district', 'town'] },
    { field: 'state', any: ['state', 'province'] },
    { field: 'contactPerson', any: ['contactperson', 'contactname', 'person', 'contact'] },

    // Last: the name of the account itself, the most generic thing on a sheet.
    { field: 'company', any: ['partyname', 'companyname', 'customername', 'clientname', 'firmname', 'accountname', 'company', 'party', 'client', 'customer', 'name'] },
];

/**
 * A first guess at the map, for the company to correct.
 *
 * First header wins a field: a sheet with two columns matching the same rule
 * gets the left-hand one, and the second is left unmapped and visible rather
 * than silently overwriting the first.
 */
export function suggestMapping(headers: readonly string[]): ColumnMap {
    const map: ColumnMap = {};
    const taken = new Set<string>();

    for (const raw of headers) {
        const key = headerKey(raw);
        if (!key || taken.has(raw)) continue;

        const rule = HEADER_RULES.find(r => r.any.some(name => key.includes(name)) || r.exact?.includes(key));
        if (!rule || !rule.field) continue;         // excluded, or nothing matched
        if (map[rule.field] !== undefined) continue; // that field already has a column

        map[rule.field] = raw;
        taken.add(raw);
    }

    return map;
}

export type MappingProblemKind =
    | 'missing-required'
    | 'unknown-header'
    | 'duplicate-column'
    /** A column the chosen amount model needs, with nothing chosen for it. */
    | 'model-needs-column'
    /** A column mapped that the chosen amount model would never consult. */
    | 'model-ignores-column';

export interface MappingProblem {
    kind: MappingProblemKind;
    field?: SheetField;
    header?: string;
    message: string;
}

/**
 * Everything wrong with a map, before it is saved.
 *
 * Checked against the headers actually present, because a map saved against
 * last month's sheet is exactly how an import silently reads the wrong column
 * after somebody renames one.
 */
export function mappingProblems(
    headers: readonly string[],
    map: ColumnMap,
    model: AmountModel = DEFAULT_AMOUNT_MODEL,
): MappingProblem[] {
    const problems: MappingProblem[] = [];
    const present = new Set(headers);
    const usedBy = new Map<string, SheetField[]>();

    // The amount column is required only by the models that read one. A sheet
    // that keeps debits and credits in two columns has no single balance
    // column, and demanding one would be demanding a column that does not
    // exist; `modelProblems()` insists on the two that do.
    const readsTotal = (amountModelDefinition(model)?.requires ?? ['total']).includes('total');
    const required = REQUIRED_FIELDS.filter(field => field !== 'total' || readsTotal);

    for (const field of required) {
        if (!map[field]) {
            problems.push({
                kind: 'missing-required', field,
                message: `${fieldDefinition(field)?.label ?? field} has no column chosen.`,
            });
        }
    }

    for (const [field, header] of Object.entries(map) as [SheetField, string][]) {
        if (!header) continue;
        if (!present.has(header)) {
            problems.push({
                kind: 'unknown-header', field, header,
                message: `The column “${header}” is not in this sheet any more.`,
            });
        }
        const list = usedBy.get(header);
        if (list) list.push(field); else usedBy.set(header, [field]);
    }

    for (const [header, fields] of usedBy) {
        if (fields.length > 1) {
            problems.push({
                kind: 'duplicate-column', header,
                message: `The column “${header}” is used for ${fields.length} different fields.`,
            });
        }
    }

    return problems;
}

/**
 * The map checked against the amount model the company chose.
 *
 * Separate from `mappingProblems` because the two ask different questions: one
 * is about the sheet, the other about the interpretation. Both have to pass
 * before an import reads a figure.
 *
 * A mapped column the model would never consult is a **problem, not a
 * courtesy**. A Dr/Cr column mapped and then read under the signed model would
 * be ignored silently, and nothing on the screen would tell the company that
 * half of what they configured was doing nothing.
 */
export function modelProblems(model: AmountModel, map: ColumnMap): MappingProblem[] {
    const definition = amountModelDefinition(model);
    if (!definition) return [];

    const problems: MappingProblem[] = [];

    for (const field of definition.requires) {
        if (!map[field]) {
            problems.push({
                kind: 'model-needs-column', field,
                message: `“${definition.label}” needs a column for ${fieldDefinition(field)?.label ?? field}.`,
            });
        }
    }

    for (const field of definition.forbids) {
        const header = map[field];
        if (header) {
            problems.push({
                kind: 'model-ignores-column', field, header,
                message: `“${definition.label}” never reads ${fieldDefinition(field)?.label ?? field}, `
                    + `so the column “${header}” would be ignored. Remove it, or choose a different model.`,
            });
        }
    }

    return problems;
}

/** A map good enough to import with, under the model the company chose. */
export const mappingIsUsable = (
    headers: readonly string[],
    map: ColumnMap,
    model: AmountModel = DEFAULT_AMOUNT_MODEL,
): boolean =>
    mappingProblems(headers, map, model).length === 0 && modelProblems(model, map).length === 0;
